import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { DatePreset } from "@/components/controls/datePresets";
import {
  DateRange,
  DateRangePicker
} from "@/components/controls/DateRangePicker";
import { normalizeDateRange, ensureDateForPicker } from "@/utils/dateRange";
import DatePresetItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/DatePresetItem";
import { DateFilterValue } from "@/components/controls/ToolbarActions/ToolbarFilterButton/types";

const CUSTOM_RANGE = "custom";

interface Props {
  filterOption: DataTableFilter;
  filters: Filters;
  setFilters: FiltersSetter;
}

const FilterDateSubmenu: React.FC<Props> = ({
  filterOption,
  filters,
  setFilters
}) => {
  const { t } = useTranslation();
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const filterKey = filterOption.key;

  const currentValue = filters[filterKey as keyof Filters] as
    | DateFilterValue
    | undefined;

  const selectedPreset = useMemo(() => {
    if (!currentValue?.dateRange) return null;
    if (typeof currentValue.dateRange === "string") {
      return currentValue.dateRange;
    }
    return CUSTOM_RANGE;
  }, [currentValue]);

  const selectedRange = useMemo(() => {
    if (!currentValue?.dateRange) return null;
    if (typeof currentValue.dateRange === "string") return null;
    return normalizeDateRange(currentValue.dateRange);
  }, [currentValue]);

  const handleSelectPreset = (preset: string) => {
    if (preset === CUSTOM_RANGE) {
      setShowCustomDatePicker(true);
      return;
    }

    if (selectedPreset === preset) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[filterKey as keyof Filters];
        return newFilters;
      });
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [filterKey]: { dateRange: preset }
    }));
  };

  const handleCustomDateRangeSelect = (range: DateRange) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: { dateRange: range }
    }));
    setShowCustomDatePicker(false);
  };

  const handleCancelCustom = () => {
    setShowCustomDatePicker(false);
  };

  if (showCustomDatePicker) {
    return (
      <DateRangePicker
        labels={{ cancel: t("cancel"), apply: t("apply") }}
        onUpdate={({ range }) => handleCustomDateRangeSelect(range)}
        onCancel={handleCancelCustom}
        initialFromDate={ensureDateForPicker(selectedRange?.fromDate)}
        initialToDate={ensureDateForPicker(selectedRange?.toDate)}
        alwaysApply
      />
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-72">
      {Object.values(DatePreset).map((preset) => (
        <DatePresetItem
          key={preset}
          label={t(`datatoolbar.filters.presets.${preset}`)}
          isSelected={selectedPreset === preset}
          onClick={() => handleSelectPreset(preset)}
        />
      ))}
      <DatePresetItem
        label={t("datatoolbar.filters.custom")}
        isSelected={selectedPreset === CUSTOM_RANGE}
        onClick={() => handleSelectPreset(CUSTOM_RANGE)}
      />
    </div>
  );
};

export default FilterDateSubmenu;
